import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { universeApi } from '../lib/api';
import { fallbackGradient, formatDuration, formatMonthYear, typeLabel } from '../lib/format';
import type { PdfThumbResult } from '../lib/pdfThumb';
import { strings } from '../lib/strings';
import type { UniverseAsset } from '../lib/types';
import { normalizeUrl, siteHost } from '../lib/web';
import { ActivityIndicator } from './ActivityIndicator';
import { PauseIcon, PlayIcon, SpeakerIcon, SpeakerMutedIcon } from './Icons';
import { RollLabel } from './RollLabel';
import { TypeGlyph, TypeTile } from './TypeGlyph';

const CARD_WIDTH = 304;
const GAP = 12;
const DOC_TYPES = new Set(['pdf', 'presentation', 'document']);

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

const clampRatio = (r: number) => Math.min(Math.max(r, 0.6), 2);
/** Artwork is cropped to a landscape frame so the card fits above or below its disc. */
const clampCover = (r: number) => Math.min(Math.max(r, 1.25), 2);

interface Props {
  asset: UniverseAsset;
  anchor: DOMRect;
  image: string | null;
  /** First page of a document without a backend preview, rendered for its disc. */
  sheet?: string | null;
  closing: boolean;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
  onOpen: () => void;
}

function VideoPreview({
  src,
  poster,
  onMeta,
}: {
  src: string;
  poster: string | null;
  onMeta: (ratio: number, duration: number) => void;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [autoplay] = useState(() => !prefersReducedMotion());

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.muted = true;
    if (autoplay) el.play().catch(() => undefined);
  }, [autoplay]);

  const toggleSound = (e: MouseEvent) => {
    e.stopPropagation();
    const el = ref.current;
    if (!el) return;
    el.muted = !muted;
    setMuted(!muted);
    if (el.paused) el.play().catch(() => undefined);
  };

  return (
    <>
      <video
        ref={ref}
        src={src}
        poster={poster ?? undefined}
        loop
        playsInline
        preload={autoplay ? 'auto' : 'metadata'}
        data-playing={playing}
        className="preview-video absolute inset-0 h-full w-full object-cover"
        onLoadedMetadata={(e) => {
          const { videoWidth, videoHeight, duration } = e.currentTarget;
          if (videoWidth && videoHeight) onMeta(clampRatio(videoWidth / videoHeight), duration);
        }}
        onPlaying={() => setPlaying(true)}
        onTimeUpdate={(e) => {
          const { currentTime, duration } = e.currentTarget;
          if (duration) setProgress(currentTime / duration);
        }}
      />
      {autoplay && !playing && (
        <span className="absolute left-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-glass text-white">
          <ActivityIndicator size="sm" />
        </span>
      )}
      <button
        type="button"
        tabIndex={-1}
        onClick={toggleSound}
        className="pressable glass-chip absolute bottom-3 right-3 flex h-8 w-8 items-center justify-center rounded-full text-white"
      >
        {muted ? <SpeakerMutedIcon className="h-4 w-4" /> : <SpeakerIcon className="h-4 w-4" />}
      </button>
      <span className="absolute inset-x-0 bottom-0 h-[3px] bg-white/20">
        <span
          className="block h-full origin-left bg-white"
          style={{ transform: `scaleX(${progress})`, transition: 'transform 250ms linear' }}
        />
      </span>
    </>
  );
}

function AudioPreview({
  src,
  artwork,
  title,
  onDuration,
}: {
  src: string;
  artwork: string | null;
  title: string;
  onDuration: (duration: number) => void;
}) {
  const ref = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const toggle = (e: MouseEvent) => {
    e.stopPropagation();
    const el = ref.current;
    if (!el) return;
    if (el.paused) el.play().catch(() => undefined);
    else el.pause();
  };

  return (
    <>
      <audio
        ref={ref}
        src={src}
        preload="metadata"
        onLoadedMetadata={(e) => onDuration(e.currentTarget.duration)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(e) => {
          const { currentTime, duration } = e.currentTarget;
          if (duration) setProgress(currentTime / duration);
        }}
      />
      {artwork ? (
        <img src={artwork} alt="" className="preview-page absolute inset-0 h-full w-full object-cover" />
      ) : (
        <span
          className="absolute inset-0 flex items-center justify-center text-white/90"
          style={{ background: fallbackGradient(title) }}
        >
          <TypeGlyph type="audio" style={{ width: 56, height: 56 }} />
        </span>
      )}
      <button
        type="button"
        tabIndex={-1}
        onClick={toggle}
        aria-label={playing ? strings.pause : strings.play}
        className="pressable glass-chip absolute bottom-3 right-3 flex h-12 w-12 items-center justify-center rounded-full text-white"
      >
        {playing ? <PauseIcon className="h-5 w-5" /> : <PlayIcon className="ml-[2px] h-5 w-5" />}
      </button>
      <span className="absolute inset-x-0 bottom-0 h-[3px] bg-white/20">
        <span
          className="block h-full origin-left bg-white"
          style={{ transform: `scaleX(${progress})`, transition: 'transform 250ms linear' }}
        />
      </span>
    </>
  );
}

/** Interactive floating preview: stays open while the pointer rests on it. */
export function HoverPreview({ asset, anchor, image, sheet = null, closing, onPointerEnter, onPointerLeave, onOpen }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [pos, setPos] = useState<{ top: number; left: number; origin: string } | null>(null);
  const [ratio, setRatio] = useState<number | null>(null);
  const [detail, setDetail] = useState<string | null>(null);
  const [page, setPage] = useState<PdfThumbResult | null>(null);
  const [pageFailed, setPageFailed] = useState(false);

  const [audioSrc, setAudioSrc] = useState<string | null>(asset.type === 'audio' ? asset.playback_url : null);
  const [resolvedType, setResolvedType] = useState(asset.type);
  const type = audioSrc ? 'audio' : resolvedType;
  const maybeMedia = asset.type === 'audio' || (asset.type === 'document' && !asset.preview_url);
  const isDoc = DOC_TYPES.has(type) && !maybeMedia;
  const [videoSrc, setVideoSrc] = useState<string | null>(asset.type === 'video' ? asset.playback_url : null);
  const linkUrl = asset.type === 'link' ? normalizeUrl(asset.external_url) : null;

  useEffect(() => {
    if (!maybeMedia || audioSrc) return;
    let alive = true;
    universeApi
      .getAsset(asset.id)
      .then((d) => {
        if (!alive) return;
        setResolvedType(d.type);
        const src = d.playback_url ?? d.file_url;
        if (d.type === 'audio' && src) setAudioSrc(src);
        if (d.type === 'video' && src) setVideoSrc(src);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [asset.id, audioSrc, maybeMedia]);

  useEffect(() => {
    if (asset.type !== 'video' || asset.playback_url) return;
    let alive = true;
    universeApi
      .getAsset(asset.id)
      .then((d) => alive && setVideoSrc(d.playback_url ?? d.file_url))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [asset.id, asset.playback_url, asset.type]);

  useEffect(() => {
    if (!isDoc) return;
    let alive = true;
    const source = asset.preview_url
      ? Promise.resolve(asset.preview_url)
      : asset.type === 'pdf'
        ? universeApi.getAsset(asset.id).then((d) => d.preview_url ?? d.file_url)
        : Promise.resolve(null);
    source
      .then(async (url) => {
        if (!alive) return;
        if (!url) {
          setPageFailed(true);
          return;
        }
        const { renderFirstPage } = await import('../lib/pdfThumb');
        const result = await renderFirstPage(url, CARD_WIDTH);
        if (!alive) return;
        setPage(result);
        setDetail(strings.pages(result.pages));
      })
      .catch(() => alive && setPageFailed(true));
    return () => {
      alive = false;
    };
  }, [asset.id, asset.preview_url, asset.type, isDoc]);

  // Media (PDF page, video metadata, artwork) resolves after first paint and changes the card's
  // height, so placement is re-run on every resize; the chosen side is kept while it still fits.
  const side = useRef<'below' | 'above' | 'beside' | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    side.current = null;

    const place = () => {
      const height = el.offsetHeight;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const clampX = (x: number) => Math.max(GAP, Math.min(x, vw - CARD_WIDTH - GAP));
      const clampY = (y: number) => Math.max(GAP, Math.min(y, vh - height - GAP));
      const centeredLeft = clampX(anchor.left + anchor.width / 2 - CARD_WIDTH / 2);
      const originX = `${anchor.left + anchor.width / 2 - centeredLeft}px`;

      const fitsBelow = anchor.bottom + GAP + height <= vh - GAP;
      const fitsAbove = anchor.top - GAP - height >= GAP;
      const current = side.current;
      const next =
        (current === 'below' && fitsBelow) || (current === 'above' && fitsAbove)
          ? current
          : fitsBelow
            ? 'below'
            : fitsAbove
              ? 'above'
              : 'beside';
      side.current = next;

      if (next === 'below') {
        setPos({ top: anchor.bottom + GAP, left: centeredLeft, origin: `${originX} 0` });
      } else if (next === 'above') {
        setPos({ top: anchor.top - GAP - height, left: centeredLeft, origin: `${originX} 100%` });
      } else {
        const right = anchor.right + GAP;
        const toRight = right + CARD_WIDTH <= vw - GAP;
        setPos({
          top: clampY(anchor.top + anchor.height / 2 - height / 2),
          left: clampX(toRight ? right : anchor.left - GAP - CARD_WIDTH),
          origin: toRight ? '0 50%' : '100% 50%',
        });
      }
    };

    place();
    const observer = new ResizeObserver(place);
    observer.observe(el);
    window.addEventListener('resize', place);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', place);
    };
  }, [anchor]);

  const open = () => {
    onOpen();
    navigate(`/view/${asset.id}`, { state: { background: location } });
  };

  const updated = formatMonthYear(asset.updated_at);
  const meta = [typeLabel(type), detail ?? (linkUrl ? siteHost(linkUrl) : null)].filter(Boolean).join(' · ');
  const pageLoading = isDoc && !page && !pageFailed;
  const hasMedia = Boolean(audioSrc || videoSrc || image || page || pageLoading || sheet);
  const portraitPage = page ? page.ratio < 1 : false;
  const frameRatio = page
    ? portraitPage
      ? '4 / 3'
      : String(clampRatio(page.ratio))
    : ratio
      ? String(ratio)
      : isDoc || sheet
        ? '4 / 3'
        : '16 / 9';
  const shown = Boolean(pos) && !closing;

  return createPortal(
    <div
      ref={ref}
      aria-hidden="true"
      data-closing={closing}
      onMouseEnter={onPointerEnter}
      onMouseLeave={onPointerLeave}
      onClick={open}
      className="hover-preview group/preview fixed z-popover cursor-pointer overflow-hidden rounded-md bg-canvas shadow-overlay ring-1 ring-black/5"
      style={{
        width: CARD_WIDTH,
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        opacity: shown ? 1 : 0,
        transform: shown ? 'none' : 'scale(0.94)',
        transformOrigin: pos?.origin ?? '50% 0',
        pointerEvents: closing ? 'none' : 'auto',
      }}
    >
      <div
        className="preview-frame relative overflow-hidden"
        style={{
          aspectRatio: frameRatio,
          background: hasMedia ? 'var(--color-surface)' : fallbackGradient(asset.title),
        }}
      >
        {page ? (
          portraitPage ? (
            <>
              {image && (
                <img
                  src={image}
                  alt=""
                  className="absolute inset-0 h-full w-full scale-110 object-cover"
                  style={{ filter: 'blur(22px) saturate(1.3) brightness(0.9)' }}
                />
              )}
              <img
                src={page.src}
                alt=""
                className="preview-page preview-sheet absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white"
                style={{ height: 'calc(100% - 32px)', aspectRatio: String(page.ratio) }}
              />
            </>
          ) : (
            <img src={page.src} alt="" className="preview-page absolute inset-0 h-full w-full object-cover" />
          )
        ) : audioSrc ? (
          <AudioPreview
            src={audioSrc}
            artwork={image}
            title={asset.title}
            onDuration={(duration) => {
              if (Number.isFinite(duration) && duration > 0) setDetail(formatDuration(duration));
            }}
          />
        ) : videoSrc ? (
          <VideoPreview
            src={videoSrc}
            poster={image}
            onMeta={(r, duration) => {
              setRatio(r);
              if (Number.isFinite(duration) && duration > 0) setDetail(formatDuration(duration));
            }}
          />
        ) : image ? (
          <img
            src={image}
            alt=""
            decoding="async"
            className="preview-page absolute inset-0 h-full w-full object-cover"
            onLoad={(e) => {
              const { naturalWidth, naturalHeight } = e.currentTarget;
              if (naturalWidth && naturalHeight) setRatio(clampCover(naturalWidth / naturalHeight));
            }}
          />
        ) : sheet ? (
          <span className="absolute inset-0 flex items-center justify-center p-4">
            <img src={sheet} alt="" className="preview-page max-h-full max-w-full rounded-[4px] bg-white shadow-raised" />
          </span>
        ) : pageLoading ? (
          <div className="skeleton absolute inset-0" />
        ) : (
          <span className="absolute inset-0 flex items-center justify-center">
            <TypeTile type={type} size="lg" className="shadow-raised" />
          </span>
        )}
        {asset.type === 'video' && !videoSrc && (
          <span className="glass-chip absolute left-1/2 top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-white">
            <TypeGlyph type="video" style={{ width: 20, height: 20, marginLeft: 2 }} />
          </span>
        )}
      </div>

      <div className="p-4">
        <div className="line-clamp-2 text-base font-semibold leading-snug tracking-tight text-ink">
          {asset.title}
        </div>
        {asset.description && <p className="mt-1 line-clamp-2 text-sm text-muted">{asset.description}</p>}
        <div className="mt-3 flex items-center gap-2">
          <TypeTile type={type} size="sm" />
          <div className="min-w-0 flex-1 text-xs leading-tight">
            <div className="truncate font-medium text-ink">{meta}</div>
            {updated && <div className="truncate text-subtle">{strings.updated(updated)}</div>}
          </div>
          <button
            type="button"
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              open();
            }}
            className="cta pressable shrink-0 rounded-full bg-fill px-4 py-1 text-sm font-semibold text-primary [--cta-fill:var(--color-primary)]"
          >
            <RollLabel>{strings.open}</RollLabel>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
